import { Clock, Layers3 } from "lucide-react";
import { formatRupiah } from "@/lib/api";

// Komponen bernilai nihil (0) disembunyikan dari rincian slip.
const nz = (baris) => (baris || []).filter((b) => Math.round(Number(b?.nilai) || 0) !== 0);

// Palet warna untuk badge Grup Laporan (mirror `report_groups.warna`).
const GRUP_TONE = {
  teal: "bg-teal-50 text-teal-800 border-teal-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  pink: "bg-pink-50 text-pink-800 border-pink-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rose: "bg-rose-50 text-rose-800 border-rose-200",
  indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
  violet: "bg-violet-50 text-violet-800 border-violet-200",
  orange: "bg-orange-50 text-orange-800 border-orange-200",
  sky: "bg-sky-50 text-sky-800 border-sky-200",
  gray: "bg-gray-100 text-gray-700 border-gray-200",
};

/** Badge kecil menampilkan Grup Laporan sebuah baris komponen. */
function GrupBadge({ komponen_id, komponenMap }) {
  if (!komponen_id || !komponenMap) return null;
  const meta = komponenMap[komponen_id];
  if (!meta || !meta.grup_nama) return null;
  const tone = GRUP_TONE[meta.grup_warna] || GRUP_TONE.slate;
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider ${tone}`}
      title={`Grup Laporan: ${meta.grup_nama}`}
      data-testid={`slip-grup-badge-${komponen_id}`}
    >
      <Layers3 size={9} />
      {meta.grup_nama}
    </span>
  );
}

export function SlipRincian({ slip: s, showBeban = false, testId, komponenMap = null }) {
  if (!s) return null;
  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Mini label="Hari Kerja" value={s.hari_kerja_periode} />
        <Mini label="Hadir" value={s.hari_hadir} />
        <Mini label="Terlambat" value={s.hari_terlambat} />
        <Mini label="Alpha" value={s.hari_alpha} />
        <Mini label="Jam Lembur" value={s.total_jam_lembur} icon={Clock} />
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Rincian
            title="Pendapatan Tetap"
            tone="emerald"
            baris={[{ nama: "Gaji Pokok", nilai: s.gaji_pokok }, ...nz(s.pendapatan_tetap)]}
            totalLabel="Subtotal Tetap"
            total={s.bruto_tetap}
            testId={`slip-tetap-${s.id}`}
            komponenMap={komponenMap}
          />
          <Rincian
            title="Pendapatan Tidak Tetap"
            tone="sky"
            baris={nz(s.pendapatan_tidak_tetap)}
            totalLabel="Subtotal Tidak Tetap"
            total={s.bruto_tidak_tetap}
            testId={`slip-tidak-tetap-${s.id}`}
            komponenMap={komponenMap}
          />
        </div>
        <div className="space-y-4">
          <Rincian
            title="Potongan"
            tone="rose"
            baris={[
              ...nz(s.potongan),
              ...(s.pph21_dipotong
                ? [{ nama: "PPh 21", nilai: s.pph21_dipotong, catatan: `PTKP ${s.ptkp_status}` }]
                : []),
            ]}
            totalLabel="Total Potongan"
            total={s.total_potongan}
            testId={`slip-potongan-${s.id}`}
            komponenMap={komponenMap}
          />
          {showBeban ? (
            <Rincian
              title="Beban Perusahaan"
              tone="indigo"
              baris={nz(s.beban_perusahaan)}
              totalLabel="Total Beban Perusahaan"
              total={s.total_beban_perusahaan}
              testId={`slip-beban-${s.id}`}
              komponenMap={komponenMap}
            />
          ) : null}
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
          Total Pendapatan (Bruto)
        </span>
        <span className="text-sm font-extrabold text-slate-900" data-testid={`slip-bruto-${s.id}`}>
          {formatRupiah(s.bruto)}
        </span>
      </div>

      <div className="mt-3 rounded-lg bg-teal-700 text-white px-4 py-3 flex items-center justify-between">
        <span className="text-sm font-bold uppercase tracking-wider">Gaji Bersih Diterima</span>
        <span className="text-lg font-extrabold">{formatRupiah(s.netto)}</span>
      </div>

      {s.pph21_ditanggung_perusahaan ? (
        <div
          className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-xs text-indigo-900"
          data-testid={`slip-pph-perusahaan-${s.id}`}
        >
          <b>PPh 21 {formatRupiah(s.pph21_ditanggung_perusahaan)} ditanggung perusahaan</b> — tidak
          dipotong dari gaji bersih.
        </div>
      ) : null}

      <p className="text-[11px] text-slate-500 mt-3">
        Metode lembur: {s.lembur_metode}. PPh 21 progresif Pasal 17 (PKP setahun{" "}
        {formatRupiah(s.pkp_setahun)}, PTKP {s.ptkp_status} {formatRupiah(s.ptkp_setahun)}).
        Rekening {s.nama_bank || "—"} {s.no_rekening || ""}.
      </p>
    </div>
  );
}

function Mini({ label, value, icon: Icon }) {
  return (
    <div className="rounded-md bg-white border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold flex items-center gap-1">
        {Icon ? <Icon size={11} /> : null} {label}
      </div>
      <div className="text-lg font-extrabold text-slate-900 mt-1 leading-none font-mono-clock">
        {value ?? 0}
      </div>
    </div>
  );
}

function Rincian({ title, tone, baris, totalLabel, total, testId, komponenMap = null }) {
  const heads = {
    rose: "bg-rose-50 text-rose-800 border-rose-100",
    sky: "bg-sky-50 text-sky-800 border-sky-100",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    indigo: "bg-indigo-50 text-indigo-800 border-indigo-100",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={testId}>
      <div className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b ${heads[tone] || heads.emerald}`}>
        {title}
      </div>
      <div className="divide-y divide-slate-100">
        {baris.length === 0 ? (
          <div className="px-4 py-3 text-xs text-slate-400 italic">Tidak ada</div>
        ) : (
          baris.map((b, i) => (
            <div key={i} className="px-4 py-2.5 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-800 flex items-center gap-1.5 flex-wrap">
                  <span>{b.nama}</span>
                  <GrupBadge komponen_id={b.komponen_id} komponenMap={komponenMap} />
                </div>
                {b.catatan ? <div className="text-[10px] text-slate-400">{b.catatan}</div> : null}
                {b.breakdown ? (
                  <div
                    className="mt-1.5 rounded-md bg-slate-50 border border-slate-100 p-2 text-[10px] text-slate-600 leading-relaxed space-y-0.5"
                    data-testid="potongan-absensi-breakdown"
                  >
                    <div>
                      <b>Dasar potongan absensi:</b>{" "}
                      {formatRupiah(b.breakdown.dasar_total)} (Σ{" "}
                      {(b.breakdown.dasar_items || []).map((it) => it.nama).join(", ") || "—"})
                    </div>
                    <div>
                      Alpha: <b>{b.breakdown.hari_alpha}</b> hari × {b.breakdown.persen_alpha}% ={" "}
                      <b>{(b.breakdown.hari_alpha * b.breakdown.persen_alpha).toFixed(2)}%</b>
                    </div>
                    <div>
                      Terlambat: <b>{b.breakdown.hari_terlambat}</b> hari ×{" "}
                      {b.breakdown.persen_terlambat}% ={" "}
                      <b>{(b.breakdown.hari_terlambat * b.breakdown.persen_terlambat).toFixed(2)}%</b>
                    </div>
                    <div>
                      Total: <b>{b.breakdown.persen_total}%</b> ×{" "}
                      {formatRupiah(b.breakdown.dasar_total)} ={" "}
                      <b>{formatRupiah(b.breakdown.nilai_sebelum_cap)}</b>
                    </div>
                    {b.breakdown.nilai_sebelum_cap > b.breakdown.cap_maksimal ? (
                      <div className="text-rose-600">
                        Cap maksimal (= total dasar): {formatRupiah(b.breakdown.cap_maksimal)} — nilai
                        di-clamp.
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {b.detail_klaim && b.detail_klaim.length ? (
                  <div
                    className="mt-1.5 rounded-md bg-sky-50 border border-sky-100 p-2 text-[10px] text-sky-800 leading-relaxed space-y-0.5"
                    data-testid="klaim-breakdown"
                  >
                    <div className="font-semibold uppercase tracking-wider text-[9px] mb-0.5">
                      Rincian Klaim ({b.detail_klaim.length})
                    </div>
                    {b.detail_klaim.map((k, ki) => (
                      <div key={ki}>
                        • {k.tanggal} — {k.kategori_nama}: <b>{formatRupiah(k.nilai)}</b>
                        {k.keterangan ? (
                          <span className="text-sky-700/70"> — {k.keterangan}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="text-sm font-semibold text-slate-900 whitespace-nowrap">
                {formatRupiah(b.nilai)}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="px-4 py-2.5 bg-slate-50 flex items-center justify-between border-t border-slate-100">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{totalLabel}</span>
        <span className="text-sm font-extrabold text-slate-900">{formatRupiah(total)}</span>
      </div>
    </div>
  );
}
